// src/components/map/hooks/useAccountsMapData.js
import { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { chunk, getSampleAccountId, STATUS_CONVERTED } from "../utils/mapHelpers";
import { getUserTerritoryIds } from "../../../utils/accountTerritory";

export const useAccountsMapData = (companyId) => {
  const auth = getAuth();

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState("");

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");

  const [territories, setTerritories] = useState([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(true);
  const [territoriesError, setTerritoriesError] = useState("");

  const [sampleAccountIds, setSampleAccountIds] = useState(new Set());
  const [sampleCountsByAccount, setSampleCountsByAccount] = useState({});
  const [convertedCountsByAccount, setConvertedCountsByAccount] = useState({});
  const [sampleSummariesByAccount, setSampleSummariesByAccount] = useState({});
  const [samplesLoading, setSamplesLoading] = useState(true);
  const [samplesError, setSamplesError] = useState("");

  const [myProfile, setMyProfile] = useState(null);

  const myRole = (myProfile?.role || "").toString().toLowerCase();
  const isAdmin = myRole === "admin" || myRole === "owner";

  const myUid = auth.currentUser?.uid || myProfile?.uid || myProfile?.id || null;

  const myTerritoryIds = useMemo(() => getUserTerritoryIds(myProfile), [myProfile]);

  // Load my profile
  useEffect(() => {
    if (!companyId) return;

    const loadMe = async () => {
      const uid = auth.currentUser?.uid || null;
      if (!uid) {
        setMyProfile(null);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", uid));
        setMyProfile(snap.exists() ? snap.data() || null : null);
      } catch (err) {
        console.warn("[useAccountsMapData] Failed to load my profile:", err);
        setMyProfile(null);
      }
    };

    loadMe();
  }, [companyId, auth]);

  // Load accounts (rules-safe)
  useEffect(() => {
    if (!companyId) return;

    const fetchAccounts = async () => {
      setAccountsLoading(true);
      setAccountsError("");

      const uid = myUid;
      if (!uid) {
        setAccounts([]);
        setAccountsLoading(false);
        return;
      }

      try {
        const colRef = collection(db, "companies", companyId, "accounts");

        if (isAdmin) {
          const snap = await getDocs(colRef);
          setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          return;
        }

        const map = new Map();
        const addSnap = (snap) =>
          snap.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));

        try {
          addSnap(await getDocs(query(colRef, where("assignedToId", "==", uid))));
        } catch (e) {}

        try {
          addSnap(await getDocs(query(colRef, where("createdBy", "==", uid))));
        } catch (e) {}

        try {
          addSnap(await getDocs(query(colRef, where("sharedWithUserIds", "array-contains", uid))));
        } catch (e) {}

        try {
          addSnap(await getDocs(query(colRef, where("visibleToUserIds", "array-contains", uid))));
        } catch (e) {}

        const territoryChunks = chunk(myTerritoryIds, 10);
        for (const c of territoryChunks) {
          if (!c.length) continue;
          try {
            addSnap(await getDocs(query(colRef, where("territoryId", "in", c))));
          } catch (e) {}
        }

        setAccounts(Array.from(map.values()));
      } catch (err) {
        console.error("[useAccountsMapData] Failed to load accounts:", err);
        setAccountsError("Failed to load accounts.");
        setAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    fetchAccounts();
  }, [companyId, auth, isAdmin, myTerritoryIds, myUid]);

  // Load users (rules-safe)
  useEffect(() => {
    if (!companyId) return;

    const fetchUsers = async () => {
      setUsersLoading(true);
      setUsersError("");

      const uid = auth.currentUser?.uid || null;
      if (!uid) {
        setUsers([]);
        setUsersLoading(false);
        return;
      }

      try {
        if (!isAdmin) {
          const me = myProfile ? { id: uid, ...myProfile } : { id: uid };
          setUsers([me]);
          return;
        }

        const colRef = collection(db, "users");
        const qUsers = query(colRef, where("companyId", "==", companyId));
        const snap = await getDocs(qUsers);
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("[useAccountsMapData] Failed to load users:", err);
        setUsersError("Failed to load users for filters.");
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [companyId, auth, isAdmin, myProfile]);

  // Load territories (rules-safe)
  useEffect(() => {
    if (!companyId) return;

    const fetchTerritories = async () => {
      setTerritoriesLoading(true);
      setTerritoriesError("");

      try {
        const colRef = collection(db, "companies", companyId, "territories");

        if (isAdmin) {
          const snap = await getDocs(colRef);
          setTerritories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          return;
        }

        if (!myTerritoryIds.length) {
          setTerritories([]);
          return;
        }

        const map = new Map();

        for (const territoryId of myTerritoryIds) {
          try {
            const snap = await getDoc(doc(db, "companies", companyId, "territories", territoryId));
            if (snap.exists()) {
              map.set(snap.id, { id: snap.id, ...snap.data() });
            }
          } catch (e) {}
        }

        setTerritories(Array.from(map.values()));
      } catch (err) {
        console.error("[useAccountsMapData] Failed to load territories:", err);
        setTerritoriesError("Failed to load territories.");
        setTerritories([]);
      } finally {
        setTerritoriesLoading(false);
      }
    };

    fetchTerritories();
  }, [companyId, isAdmin, myTerritoryIds]);

  // Load sample orders
  useEffect(() => {
    if (!companyId) return;

    const fetchSampleOrders = async () => {
      setSamplesLoading(true);
      setSamplesError("");

      try {
        const visibleAccIds = (accounts || []).map((a) => a.id).filter(Boolean);

        if (!visibleAccIds.length) {
          setSampleAccountIds(new Set());
          setSampleCountsByAccount({});
          setConvertedCountsByAccount({});
          setSampleSummariesByAccount({});
          return;
        }

        const colRef = collection(db, "companies", companyId, "sampleOrders");

        const accIdSet = new Set();
        const accCounts = {};
        const accConverted = {};
        const accSummaries = {};

        const chunks10 = chunk(visibleAccIds, 10);

        for (const c of chunks10) {
          const qReq = query(colRef, where("accountId", "in", c));
          const snap = await getDocs(qReq);

          snap.docs.forEach((d) => {
            const data = d.data() || {};
            const accId = getSampleAccountId(data);
            if (!accId) return;

            accIdSet.add(accId);
            accCounts[accId] = (accCounts[accId] || 0) + 1;

            const status = String(data.status || "");
            const isConverted =
              status === STATUS_CONVERTED ||
              data.convertedToOrder === true ||
              !!data.orderNumber;

            if (isConverted) accConverted[accId] = (accConverted[accId] || 0) + 1;

            const label =
              data.productName ||
              (Array.isArray(data.items) && data.items[0]?.productName) ||
              "Sample request";

            const createdAtSeconds =
              data.createdAt?.seconds ||
              (typeof data.createdAt?.toDate === "function"
                ? Math.floor(data.createdAt.toDate().getTime() / 1000)
                : 0);

            if (!accSummaries[accId]) accSummaries[accId] = [];
            accSummaries[accId].push({
              id: d.id,
              label,
              status: status || null,
              createdAtSeconds,
              isConverted,
            });
          });
        }

        Object.keys(accSummaries).forEach((accId) => {
          accSummaries[accId] = accSummaries[accId]
            .sort((a, b) => (b.createdAtSeconds || 0) - (a.createdAtSeconds || 0))
            .slice(0, 5);
        });

        setSampleAccountIds(accIdSet);
        setSampleCountsByAccount(accCounts);
        setConvertedCountsByAccount(accConverted);
        setSampleSummariesByAccount(accSummaries);
      } catch (err) {
        console.error("[useAccountsMapData] Failed to load sample requests:", err);
        setSamplesError("Failed to load sample order data.");
        setSampleAccountIds(new Set());
        setSampleCountsByAccount({});
        setConvertedCountsByAccount({});
        setSampleSummariesByAccount({});
      } finally {
        setSamplesLoading(false);
      }
    };

    fetchSampleOrders();
  }, [companyId, accounts]);

  return {
    accounts,
    accountsLoading,
    accountsError,

    users,
    usersLoading,
    usersError,

    territories,
    setTerritories,
    territoriesLoading,
    territoriesError,

    sampleData: {
      sampleAccountIds,
      sampleCountsByAccount,
      convertedCountsByAccount,
      sampleSummariesByAccount,
    },
    samplesLoading,
    samplesError,

    myProfile,
    isAdmin,
  };
};
